import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarClock, CheckCircle2, Loader2 } from "lucide-react";
import { fetchAvailability, createBooking } from "@/lib/api";

const TIME_SLOTS = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30"];

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: "",
  vehicle_color: "",
  address: "",
  notes: "",
};

export default function Booking({ services, selectedSlug, setSelectedSlug }) {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [taken, setTaken] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const selectedService = useMemo(
    () => services.find((s) => s.slug === selectedSlug) || null,
    [services, selectedSlug]
  );

  useEffect(() => {
    if (!date) return;
    const ds = format(date, "yyyy-MM-dd");
    fetchAvailability(ds)
      .then((res) => setTaken(res.taken || []))
      .catch(() => setTaken([]));
    setTime("");
  }, [date]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlug) return toast.error("Please choose a package");
    if (!date) return toast.error("Please pick a date");
    if (!time) return toast.error("Please pick a time slot");

    const required = ["full_name", "email", "phone", "vehicle_make", "vehicle_model", "vehicle_year", "address"];
    for (const k of required) {
      if (!form[k]?.trim()) return toast.error(`Please fill in ${k.replace("_", " ")}`);
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_slug: selectedSlug,
        date: format(date, "yyyy-MM-dd"),
        time,
      };
      const data = await createBooking(payload);
      setConfirmation(data);
      toast.success("Booking confirmed!");
      setForm(initialForm);
      setDate(undefined);
      setTime("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (confirmation) {
    return (
      <section id="booking" className="relative bg-[#050505] py-28 md:py-36 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-[#0a0a0c] border border-blue-500/30 p-10 md:p-14 text-center">
            <CheckCircle2 size={56} className="mx-auto text-blue-400 mb-6" />
            <span className="text-blue-500 uppercase text-xs tracking-[0.3em] font-semibold mb-4 block">
              Booking confirmed
            </span>
            <h2 className="font-heading text-white text-4xl md:text-5xl tracking-tighter font-light mb-4">
              See you soon,
              <br />
              <span className="font-medium">{confirmation.full_name.split(" ")[0]}.</span>
            </h2>
            <p className="text-zinc-400 max-w-md mx-auto mb-8">
              We've reserved your spot. You'll receive a confirmation at{" "}
              <span className="text-white">{confirmation.email}</span> shortly.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-8">
              <Detail label="Package" value={confirmation.service_name} />
              <Detail label="Total (pay on-site)" value={`$${confirmation.service_price}`} />
              <Detail label="Date" value={confirmation.date} />
              <Detail label="Time" value={confirmation.time} />
              <Detail
                label="Vehicle"
                value={`${confirmation.vehicle_year} ${confirmation.vehicle_make} ${confirmation.vehicle_model}`}
              />
              <Detail label="Address" value={confirmation.address} />
            </div>

            <button
              data-testid="new-booking-btn"
              onClick={() => {
                setConfirmation(null);
                setSelectedSlug(null);
              }}
              className="border border-white/20 hover:border-white/40 hover:bg-white/5 text-white px-8 py-4 text-sm font-medium tracking-wide transition-all"
            >
              Book another
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="booking" className="relative bg-[#050505] py-28 md:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-14 max-w-2xl">
          <span className="text-blue-500 uppercase text-xs tracking-[0.3em] font-semibold mb-4 block">
            · 07 Book your detail
          </span>
          <h2 className="font-heading text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light">
            Reserve in
            <br />
            <span className="font-medium">under sixty seconds.</span>
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT: Form fields */}
          <div className="lg:col-span-7 bg-[#0a0a0c] border border-white/8 p-8 md:p-10 space-y-8">
            <FieldGroup title="01 · Choose a package">
              <Select value={selectedSlug || ""} onValueChange={(v) => setSelectedSlug(v)}>
                <SelectTrigger
                  data-testid="service-select"
                  className="bg-black/50 border-white/10 rounded-none text-white h-14 text-base"
                >
                  <SelectValue placeholder="Select a detailing package…" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-white/10 text-white">
                  {services.map((s) => (
                    <SelectItem
                      key={s.slug}
                      value={s.slug}
                      data-testid={`service-option-${s.slug}`}
                      className="focus:bg-blue-600/20 focus:text-white"
                    >
                      <div className="flex items-center justify-between gap-8 w-full">
                        <span>{s.name}</span>
                        <span className="text-zinc-500">${s.price}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldGroup>

            <FieldGroup title="02 · Your details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="full_name" label="Full name" value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" />
                <Field id="email" type="email" label="Email" value={form.email} onChange={update("email")} placeholder="jane@example.com" />
                <Field id="phone" label="Phone" value={form.phone} onChange={update("phone")} placeholder="+1 555 123 4567" />
                <Field id="address" label="Service address" value={form.address} onChange={update("address")} placeholder="221B Baker St." />
              </div>
            </FieldGroup>

            <FieldGroup title="03 · Your vehicle">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="vehicle_make" label="Make" value={form.vehicle_make} onChange={update("vehicle_make")} placeholder="Porsche" />
                <Field id="vehicle_model" label="Model" value={form.vehicle_model} onChange={update("vehicle_model")} placeholder="911" />
                <Field id="vehicle_year" label="Year" value={form.vehicle_year} onChange={update("vehicle_year")} placeholder="2023" />
                <Field id="vehicle_color" label="Color (optional)" value={form.vehicle_color} onChange={update("vehicle_color")} placeholder="Midnight Black" />
              </div>
              <div className="mt-4">
                <Label htmlFor="notes" className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                  Notes (optional)
                </Label>
                <Textarea
                  id="notes"
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={update("notes")}
                  placeholder="Any specific concerns? Stains, scratches, special requests…"
                  className="bg-black/50 border-white/10 rounded-none text-white min-h-[100px] focus:border-blue-500"
                />
              </div>
            </FieldGroup>
          </div>

          {/* RIGHT: Date / Time / Summary */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-[#0a0a0c] border border-white/8 p-6">
              <div className="flex items-center gap-2 mb-4">
                <CalendarClock size={16} className="text-blue-400" />
                <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">04 · Pick a date</p>
              </div>
              <div data-testid="calendar-wrapper" className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < today}
                  className="bg-transparent"
                />
              </div>
            </div>

            <div className="bg-[#0a0a0c] border border-white/8 p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">05 · Pick a time</p>
              <div className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((slot) => {
                  const isTaken = taken.includes(slot);
                  const isSelected = time === slot;
                  const disabled = isTaken || !date;
                  return (
                    <button
                      type="button"
                      key={slot}
                      data-testid={`time-slot-${slot}`}
                      disabled={disabled}
                      onClick={() => setTime(slot)}
                      className={`py-3 text-sm font-medium tracking-wide border transition-all ${
                        isSelected
                          ? "border-blue-500 bg-blue-600 text-white"
                          : disabled
                          ? "border-white/5 bg-white/0 text-zinc-700 cursor-not-allowed line-through"
                          : "border-white/10 hover:border-white/30 text-zinc-300 bg-black/30"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
              {!date && (
                <p className="text-zinc-600 text-xs mt-3">Choose a date to see availability.</p>
              )}
            </div>

            {/* Summary */}
            <div className="bg-blue-600 p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-blue-100 mb-4">Summary</p>
              <div className="space-y-2 text-sm">
                <Row label="Package" value={selectedService?.name || "—"} />
                <Row label="Date" value={date ? format(date, "MMM d, yyyy") : "—"} />
                <Row label="Time" value={time || "—"} />
                <div className="border-t border-white/20 mt-3 pt-3 flex items-baseline justify-between">
                  <span className="text-blue-100 text-sm uppercase tracking-wider">Total · pay on-site</span>
                  <span className="font-heading text-white text-3xl font-light tracking-tighter">
                    ${selectedService?.price || 0}
                  </span>
                </div>
              </div>
              <button
                type="submit"
                data-testid="confirm-booking-btn"
                disabled={submitting}
                className="mt-6 w-full bg-black hover:bg-black/80 text-white py-4 font-medium tracking-wide flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                {submitting ? "Booking…" : "Confirm booking"}
              </button>
              <p className="text-blue-100/70 text-xs mt-3 text-center">
                No payment now. Pay our detailer at your appointment.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">{title}</p>
      {children}
    </div>
  );
}

function Field({ id, label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`input-${id}`}
        className="bg-black/50 border-white/10 rounded-none text-white h-12 focus:border-blue-500"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-blue-100/80 text-xs uppercase tracking-wider">{label}</span>
      <span className="text-white text-sm truncate max-w-[60%] text-right">{value}</span>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-black/40 border border-white/5 p-4">
      <p className="text-zinc-500 text-xs uppercase tracking-[0.2em] mb-1">{label}</p>
      <p className="text-white text-sm font-medium">{value}</p>
    </div>
  );
}
